/**
 * One of the payee Overview's bar charts (TMMAPP-0123 item 24): By envelope, By category or By
 * month. Horizontal bars as an inline SVG on tokens (no chart library), each row with its label and
 * its amount (`MoneyText`, in the payee's currency as served); a bar is the amount's magnitude
 * against the largest in the chart. The drawn chart is hidden from assistive technology and a
 * visually hidden table carries the same rows. A chart with no rows collapses to one line, with no
 * heading.
 */
import { useId, type ReactNode } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { MoneyText } from '@/components/MoneyText';
import { resolveTokens } from '@/theme/theme';

export interface PayeeBarRow {
  key: string;
  /** What the drawn chart shows (a category chip, say). */
  label: ReactNode;
  /** The label as text, for the table. */
  labelText: string;
  amountMinor: number;
}

interface PayeeBarChartProps {
  title: string;
  /** The table's first column heading ("Envelope", "Category", "Month"). */
  columnLabel: string;
  rows: PayeeBarRow[];
  currency: string | null;
  /** The one line shown when there are no rows. */
  emptyText: string;
}

const BAR_HEIGHT = 10;

export function PayeeBarChart({
  title,
  columnLabel,
  rows,
  currency,
  emptyText,
}: PayeeBarChartProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const headingId = useId();
  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }} data-chart-empty="">
        {emptyText}
      </Typography>
    );
  }
  const max = rows.reduce((m, row) => Math.max(m, Math.abs(row.amountMinor)), 0) || 1;
  return (
    <Box component="section" aria-labelledby={headingId} sx={{ minWidth: 0 }}>
      <Typography variant="h2" id={headingId} sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Stack spacing={1} aria-hidden data-bar-chart="">
        {rows.map((row) => {
          const width = (Math.abs(row.amountMinor) / max) * 100;
          return (
            <Box
              key={row.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(88px, 32%) minmax(0, 1fr) auto',
                columnGap: 1.5,
                alignItems: 'center',
              }}
            >
              <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {typeof row.label === 'string' ? (
                  <Typography variant="body2">{row.label}</Typography>
                ) : (
                  row.label
                )}
              </Box>
              <svg
                width="100%"
                height={BAR_HEIGHT}
                viewBox={`0 0 100 ${BAR_HEIGHT}`}
                preserveAspectRatio="none"
                style={{ display: 'block' }}
                focusable="false"
              >
                <rect
                  x={0}
                  y={0}
                  width={Math.max(width, 0.5)}
                  height={BAR_HEIGHT}
                  rx={2}
                  fill={tokens.brandBoundary}
                />
              </svg>
              <Box sx={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                <MoneyText minor={row.amountMinor} currency={currency} />
              </Box>
            </Box>
          );
        })}
      </Stack>
      <Box component="table" sx={visuallyHidden}>
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">{columnLabel}</th>
            <th scope="col">{t('payee.chart.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.labelText}</th>
              <td>
                <MoneyText minor={row.amountMinor} currency={currency} />
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}
